import {
  Component,
  Inject,
  ElementRef,
  ViewChild,
  signal,
  HostListener,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AppService } from '@app/app.service';
import { TruckSearch } from '@app/trucks/models/truck-search.model';
import { TemplateDialogComponent } from '@app/shared/template-dialog/template-dialog.component';
import { STATES } from '@app/shared/constants/states';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { UserPlan, UserSettings } from '@app/auth/user.model';
import { AuthService } from '@app/auth/auth.service';
import { MatDividerModule } from '@angular/material/divider';

interface LocationOption {
  id?: number;
  city: string;
  state: string;
  zip?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

type TruckSearchFilters = TruckSearch['filters'];

@Component({
  selector: 'app-truck-search-filters-dialog',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    FormsModule,
    ReactiveFormsModule,

    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,

    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatAutocompleteTrigger,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatDividerModule,
  ],
  templateUrl: './truck-search-filters-dialog.component.html',
  styleUrl: './truck-search-filters-dialog.component.scss',
})
export class TruckSearchFiltersDialogComponent {

  testing: boolean = false;

  states = STATES;

  form: FormGroup;
  templateForm: FormGroup;

  truckSearch: TruckSearch | null = null;
  isPatching = false;

  templates: any[] = [];

  originOptions: LocationOption[] = [];
  filteredOriginOptions: LocationOption[] = [];

  loadingOrigin = signal(false);

  minSearchLength = 5;
  defaultSearchRadius = 250;
  minSearchRadius = 50;
  maxSearchRadius = 750;

  @ViewChild('originInput') originInput!: ElementRef<HTMLInputElement>;
  @ViewChild('originInput', { read: MatAutocompleteTrigger })
  originTrigger!: MatAutocompleteTrigger;

  userPlan: UserPlan | null = null;
  userSettings: UserSettings | null = null;

  isMobile = window.innerWidth < 768;

  today = this.startOfDay(new Date());

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<TruckSearchFiltersDialogComponent>,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      search?: TruckSearch | null;
      filters?: Partial<TruckSearchFilters> | null;
    }
  ) {
    this.truckSearch = data?.search ?? null;

    this.userPlan = this.authService.getUserPlanFromStorage();
    this.userSettings = this.authService.getUserSettingsFromStorage();

    this.defaultSearchRadius =
      this.userSettings?.default_search_radius_miles ?? 250;

    this.maxSearchRadius =
      this.userPlan?.max_search_radius_miles ?? 750;

    this.form = this.fb.group({
      origin: [null, Validators.required],
      origin_latitude: [null, Validators.required],
      origin_longitude: [null, Validators.required],
      origin_radius: [
        null,
        [
          Validators.required,
          Validators.min(50),
          Validators.max(this.maxSearchRadius),
        ],
      ],

      available_start_date: [null, [this.notPastDateValidator()]],
      available_end_date: [null, [this.notPastDateValidator()]],

      min_well_length: [null, Validators.min(0)],
      min_weight: [null, Validators.min(0)],

      min_axle_count: [null, Validators.min(1)],
    });

    this.templateForm = this.fb.group({
      id: [null as number | null],
    });

  }

  ngOnInit(): void {
    this.patchSearchFilters();
    this.getTemplates();
  }

  patchSearchFilters(): void {
    const filters: Partial<TruckSearchFilters> | null =
      this.truckSearch?.filters ?? this.data?.filters ?? null;

    if (!filters) {
      return;
    }

    this.isPatching = true;

    this.form.patchValue(
      {
        ...filters,
        available_start_date: this.parseLocalDate(filters.available_start_date),
        available_end_date: this.parseLocalDate(filters.available_end_date),
      },
      { emitEvent: false }
    );

    setTimeout(() => {
      this.isPatching = false;
    });
  }

  private notPastDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null;

      const selected = this.startOfDay(new Date(value));

      return selected < this.today ? { pastDate: true } : null;
    };
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  filterOrigin(): void {
    if (this.isPatching) {
      return;
    }

    const value = this.originInput.nativeElement.value.toLowerCase();

    if (value.length < this.minSearchLength) {
      this.filteredOriginOptions = [];
      return;
    }

    const alreadyInOptions = this.originOptions.some(
      (opt) => this.formatLocation(opt).toLowerCase() === value
    );

    if (!alreadyInOptions) {
      this.updateOriginOptions(value);
    }

    this.filteredOriginOptions = this.originOptions.filter((opt) =>
      this.formatLocation(opt).toLowerCase().includes(value)
    );
  }

  updateOriginOptions(search: string): void {
    if (this.isPatching) {
      return;
    }

    this.loadingOrigin.set(true);

    this.appService.getLocations(search).subscribe({
      next: (res) => {
        const data = Array.isArray(res.data) ? res.data : [];

        this.originOptions = data;
        this.filteredOriginOptions = data;

        this.loadingOrigin.set(false);

        setTimeout(() => this.originTrigger.openPanel());
      },
      error: (err) => {
        console.error(err);

        this.originOptions = [];
        this.filteredOriginOptions = [];

        this.loadingOrigin.set(false);
      },
    });
  }

  formatLocation(item: LocationOption | null): string {
    if (!item) return '';

    return `${item.city}, ${item.state}${item.zip ? ` ${item.zip}` : ''}`;
  }

  onOriginSelected(item: LocationOption): void {
    this.form.patchValue({
      origin: this.formatLocation(item),
      origin_latitude: item.latitude ?? null,
      origin_longitude: item.longitude ?? null,
      origin_radius: this.defaultSearchRadius,
    });
  }

  saveSearch(): void {

    if (this.form.invalid) {

      if (this.testing) {
        this.logInvalidFields(this.form);
      }

      this.appService.showSnackBar('Please fill in all required fields', 'error');

      this.form.markAllAsTouched();

      return;
    }

    if (this.truckSearch?.id) {
      this.updateSearch();
      return;
    }

    this.createSearch();
  }

  createSearch(): void {
    this.appService.post('trucks/searches', this.getCleanFormValues()).subscribe({
      next: (res) => {
        this.dialogRef.close(res.data);
      },
      error: (err) => {
        console.error('Error:', err);
        this.appService.showSnackBar(err.error.message, 'error');
      },
    });
  }

  updateSearch(): void {
    if (!this.truckSearch?.id) {
      return;
    }

    this.appService
      .patch(`trucks/searches/${this.truckSearch.id}`, this.getCleanFormValues())
      .subscribe({
        next: (res) => {
          this.dialogRef.close(res.data);
        },
        error: (err) => {
          console.error('Error:', err);
          this.appService.showSnackBar(err.error.message, 'error');
        },
      });
  }

  clearFilters(): void {
    this.form.reset();
  }

  close(): void {
    this.dialogRef.close();
  }

  getTemplates(): void {
    this.appService.getMyTruckSearchTemplates().subscribe({
      next: (res) => {
        this.templates = res.data;
      },
      error: (err) => {
        this.appService.handleError(err);
      },
    });
  }

  addTemplate(mode: 'copy' | 'create' = 'create'): void {
    if (mode === 'copy' && !this.templateForm.get('id')?.value) {
      return;
    }

    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode,
        name: selectedTemplate?.name ?? null,
        type: 'truck search',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        filters: this.getCleanFormValues(),
      };

      this.appService.post('trucks/search-templates', payload).subscribe({
        next: (response) => {

          this.getTemplates();
          this.templateForm.get('id')?.setValue(response.data.id);
        },
        error: (err) => this.appService.handleError(err),
      });
    });
  }

  updateTemplate(): void {
    const selectedTemplate = this.templates.find(
      (t: any) => t.id === this.templateForm.get('id')?.value
    );

    if (!selectedTemplate) return;

    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      data: {
        mode: 'update',
        name: selectedTemplate.name,
        type: 'truck search',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.name) return;

      const payload = {
        name: result.name,
        filters: this.getCleanFormValues(),
      };

      this.appService
        .patch(`trucks/search-templates/${selectedTemplate.id}`, payload)
        .subscribe({
          next: (response) => {
            this.getTemplates();
          },
          error: (err) => this.appService.handleError(err),
        });
    });
  }

  deleteTemplate(): void {
    const id = this.templateForm.get('id')?.value;

    if (!id) return;

    this.appService
      .confirm('Are you sure you want to delete this template?')
      .subscribe((ok) => {
        if (!ok) return;

        this.appService.delete(`trucks/search-templates/${id}`).subscribe({
          next: (response) => {
            // this.appService.showSnackBar(response.message, 'success');
            this.templateForm.reset();
            this.getTemplates();
          },
          error: (err) => this.appService.handleError(err),
        });
      });
  }

  onTemplateChange(): void {
    const id = this.templateForm.get('id')?.value;

    if (!id) {
      this.form.reset();
      return;
    }

    const selected = this.templates.find((t: any) => t.id === id);

    if (!selected) {
      this.form.reset();
      return;
    }

    this.form.reset();
    this.form.patchValue(selected.filters ?? {});
  }

  private getCleanFormValues(): any {
  const raw = this.form.getRawValue();
  const clean: any = {};

  Object.keys(raw).forEach((key) => {
    let value = raw[key];

    if (key === 'available_start_date' || key === 'available_end_date') {
      value = this.formatDate(value);
    }

    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      clean[key] = value;
    }
  });

  return clean;
}

  private logInvalidFields(form: FormGroup): void {
    const invalidFields: Record<string, any> = {};

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control?.invalid) {
        invalidFields[key] = {
          value: control.value,
          errors: control.errors,
        };
      }
    });

    console.table(invalidFields);
  }


  private formatDate(value: any): string | null {
    if (!value) return null;

    if (typeof value === 'string') return value;

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private parseLocalDate(date: string | Date | null | undefined): Date | null {
    if (!date) return null;

    if (date instanceof Date) {
      return date;
    }

    const [year, month, day] = date.split('-').map(Number);

    return new Date(year, month - 1, day);
  }

  clearDateRange(): void {
    this.form.get('available_start_date')?.setValue(null);
    this.form.get('available_end_date')?.setValue(null);
  }
}